import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TrackingCallToAction } from "./TrackingCallToAction";
import type { Phase } from "./TrackingCallToAction";

export type Props = {
  /** The enrollment deadline date. */
  enrollmentDeadline?: Date;
  /** The date enrollment opens. */
  enrollmentStartDate?: Date;
  /** When provided with organizationId, enables navigation to outreach tab */
  programId?: number | null;
  /** When provided with programId, enables navigation to outreach tab */
  organizationId?: number;
};

function calculateDaysRemaining(target: Date): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetNormalized = new Date(target);
  targetNormalized.setHours(0, 0, 0, 0);

  const diffTime = targetNormalized.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function useOutreachNavigation(programId: number, organizationId: number) {
  const navigate = useNavigate();
  return useCallback(() => {
    navigate(
      `/program/${programId}/organization/${organizationId}/admin/outreach`,
    );
  }, [navigate, programId, organizationId]);
}

function NavigableTrackingCallToAction({
  daysRemaining,
  phase,
  programId,
  organizationId,
}: {
  daysRemaining: number;
  phase: Phase;
  programId: number;
  organizationId: number;
}) {
  const onViewOutreach = useOutreachNavigation(programId, organizationId);
  return (
    <TrackingCallToAction
      daysRemaining={daysRemaining}
      phase={phase}
      onViewOutreach={onViewOutreach}
    />
  );
}

export function TrackingCallToActionContainer({
  enrollmentDeadline,
  enrollmentStartDate,
  programId,
  organizationId,
}: Props) {
  const startDate = enrollmentStartDate || new Date(2026, 2, 2); // March 2, 2026
  const deadline = enrollmentDeadline || new Date(2026, 3, 13); // April 13, 2026

  const { phase, daysRemaining } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startNormalized = new Date(startDate);
    startNormalized.setHours(0, 0, 0, 0);

    if (today < startNormalized) {
      return {
        phase: "pre-enrollment" as Phase,
        daysRemaining: calculateDaysRemaining(startDate),
      };
    }

    return {
      phase: "enrollment" as Phase,
      daysRemaining: calculateDaysRemaining(deadline),
    };
  }, [startDate, deadline]);

  if (programId && organizationId) {
    return (
      <NavigableTrackingCallToAction
        daysRemaining={daysRemaining}
        phase={phase}
        programId={programId}
        organizationId={organizationId}
      />
    );
  }

  return <TrackingCallToAction daysRemaining={daysRemaining} phase={phase} />;
}

export default TrackingCallToActionContainer;
